import { useState } from 'react'
import { Link } from '@tanstack/react-router'
import { useTranslation } from 'react-i18next'
import { mdiChevronDown, mdiChevronRight, mdiGauge } from '@mdi/js'
import { $api } from '../../api/queryClient'
import type { MeterSupplyContract, MeterSupplyEntry } from '../../api/schema'
import { Can } from '../../contexts/PermissionsContext'
import { useToast } from '../../hooks/use-toast'
import { useContractTypes } from '../../hooks/useQueries'
import { useInvalidateMeterSupply, useMeterSupply } from '../../hooks/useMeterSupply'
import { useFormatters } from '../../lib/format'
import { Button } from '../ui/button'
import { AppIcon } from '../ui/icon'
import { MeterSupplyStatusChip } from './MeterSupplyStatusChip'

/**
 * Who supplies this meter (spec 2026-10-27-16 §2): the current supply contract
 * and its supplier, or the plain statement that the meter has none of its own,
 * with the building's contract offered when there is one. The building's
 * single water contract does not make every water meter below it covered.
 */
export function MeterSupplyCard({ org, assetUid }: { org: string; assetUid: string }) {
  const { data, isLoading, isError } = useMeterSupply(org, assetUid)
  const entry = data?.meters[0]
  if (isLoading || isError || !entry) return null
  return <MeterSupplyBody org={org} entry={entry} />
}

function MeterSupplyBody({ org, entry }: { org: string; entry: MeterSupplyEntry }) {
  const { t } = useTranslation('assets')
  const { toast } = useToast()
  const { formatDate } = useFormatters()
  const invalidate = useInvalidateMeterSupply()
  const { data: contractTypes = [] } = useContractTypes(org)
  const [historyOpen, setHistoryOpen] = useState(false)
  const [linking, setLinking] = useState<string | null>(null)
  const link = $api.useMutation('post', '/api/v1/organizations/{orgCode}/contracts/{contractUid}/assets')
  const day = (d?: string | null) => (d ? formatDate(d, { timeZone: 'UTC' }) : '')

  const supplyType = contractTypes.find((c) => c.code === 'SUPPLY')

  const linkContract = async (contractUid: string) => {
    setLinking(contractUid)
    try {
      await link.mutateAsync({
        params: { path: { orgCode: org, contractUid } },
        body: { asset_uid: entry.asset.uid },
      })
      await invalidate()
    } catch {
      toast({ title: t('meter_supply.link_error'), variant: 'destructive' })
    } finally {
      setLinking(null)
    }
  }

  const linkButton = (c: MeterSupplyContract) => (
    <Can perm="contracts.write">
      <Button
        size="sm"
        variant="outline"
        disabled={linking === c.uid}
        onClick={() => void linkContract(c.uid)}
        data-testid={`meter-supply-link-${c.uid}`}
      >
        {t('meter_supply.link')}
      </Button>
    </Can>
  )

  const current = entry.current
  return (
    <div className="rounded-lg border border-border bg-card p-4" data-testid="meter-supply-card">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <AppIcon tone="muted" path={mdiGauge} size="md" />
          <h3 className="text-sm font-medium">{t('meter_supply.title')}</h3>
        </div>
        <span data-testid="meter-supply-status" data-status={entry.status} data-in-scope={entry.in_scope}>
          <MeterSupplyStatusChip entry={entry} testId="meter-supply-status-chip" />
        </span>
      </div>

      {!entry.in_scope && (
        <p className="mt-3 text-sm text-muted-foreground" data-testid="meter-supply-out-of-scope">
          {t(`meter_supply.out_of_scope.${entry.scope_reason ?? 'submeter'}`)}
        </p>
      )}

      {entry.in_scope && current && (
        <div className="mt-3 space-y-1 text-sm">
          <div className="flex flex-wrap items-center gap-x-2">
            <span className="text-muted-foreground">{t('meter_supply.supplier')}</span>
            {current.supplier ? (
              <Link
                to="/$org/users/$userUid"
                params={{ org, userUid: current.supplier.uid }}
                className="font-medium hover:underline"
                data-testid="meter-supply-supplier"
              >
                {current.supplier.name}
              </Link>
            ) : (
              <span className="text-muted-foreground" data-testid="meter-supply-supplier">{t('meter_supply.no_supplier')}</span>
            )}
          </div>
          <div className="flex flex-wrap items-center gap-x-2">
            <span className="text-muted-foreground">{t('meter_supply.contract')}</span>
            <Link
              to="/$org/contracts/$contractUid"
              params={{ org, contractUid: current.uid }}
              className="font-medium hover:underline"
              data-testid="meter-supply-contract"
            >
              {current.name || current.code}
            </Link>
            {!current.linked && (
              <>
                <span className="text-xs text-muted-foreground">{t('meter_supply.by_delivery_point')}</span>
                {linkButton(current)}
              </>
            )}
          </div>
          {current.delivery_point_refs.length > 0 && (
            <div className="text-xs text-muted-foreground">
              {t('meter_supply.delivery_point')} {current.delivery_point_refs.join(', ')}
            </div>
          )}
          {(current.start_date || current.end_date) && (
            <div className="text-xs text-muted-foreground">
              {[day(current.start_date), day(current.end_date)].filter(Boolean).join(' → ')}
            </div>
          )}
        </div>
      )}

      {entry.in_scope && !current && (
        <div className="mt-3 space-y-2 text-sm">
          {entry.status === 'unlinked' &&
            entry.candidates.map((c) => (
              <div key={c.uid} className="flex flex-wrap items-center gap-2" data-testid={`meter-supply-candidate-${c.uid}`}>
                <span>{t('meter_supply.unlinked', { holder: c.held_by?.name ?? '', contract: c.name || c.code })}</span>
                {linkButton(c)}
              </div>
            ))}
          {entry.status === 'lapsed' && (
            <p data-testid="meter-supply-lapsed">
              {t('meter_supply.lapsed', { date: day(entry.history[0]?.end_date) })}
            </p>
          )}
          {entry.status === 'missing' && <p>{t('meter_supply.missing')}</p>}
          <Can perm="contracts.write">
            <Button asChild size="sm" variant="outline">
              <Link
                to="/$org/contracts/create"
                params={{ org }}
                search={{ asset_uid: entry.asset.uid, contract_type_uid: supplyType?.uid }}
                data-testid="meter-supply-add-contract"
              >
                {t('meter_supply.add_contract')}
              </Link>
            </Button>
          </Can>
        </div>
      )}

      {entry.history.length > 0 && (
        <div className="mt-3" data-testid="meter-supply-history">
          <button
            type="button"
            className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground"
            onClick={() => setHistoryOpen((o) => !o)}
            aria-expanded={historyOpen}
            data-testid="meter-supply-history-toggle"
          >
            <AppIcon path={historyOpen ? mdiChevronDown : mdiChevronRight} size="xs" />
            {t('meter_supply.history', { count: entry.history.length })}
          </button>
          {historyOpen && (
            <ul className="mt-1 space-y-0.5 pl-4 text-xs text-muted-foreground">
              {entry.history.map((h) => (
                <li key={h.uid}>
                  <Link to="/$org/contracts/$contractUid" params={{ org, contractUid: h.uid }} className="hover:underline">
                    {h.supplier?.name ? `${h.supplier.name} · ` : ''}
                    {h.name || h.code}
                  </Link>{' '}
                  {[day(h.start_date), day(h.end_date)].filter(Boolean).join(' → ')}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  )
}
